(ns io.daskul.tutorial.webapp.leads
  "Lead-specific detail-page slots (F-057, SMS-178).

   Registered here, alongside the product's other frontend wiring (same place
   `aggregate/configure!` is called), per the ADR's convention.

   `trial-card` is the Trial Bookings roster — originally the Lead-detail
   hub's own card (SMS-148/161), now the `:main/before` slot on the generic
   edit page so the hub can collapse onto it. It owns its own SessionAttendee
   fetch: the page's ctx doesn't fetch SA data (that's Lead-specific roster
   data, not something the generic page computes), so the slot fetches/refetches
   its own rows, keyed off the lead record ctx supplies.

   The §4.4 attendance→lead cascade happens server-side; this component's job
   is purely to notice it happened and re-pull both sides:
   - `refetch-sas!` re-pulls the lead-scoped SA list (roster rows).
   - ctx's `:refetch!` re-pulls the Lead record itself, so the page's own
     state display (sidebar status badge, generic title, etc.) advances too."
  (:require
   [re-frame.core :as rf]
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.views.detail-slots :as detail-slots]
   [io.daskul.webapp.widgets.form.lookup :as lookup]
   [io.daskul.tutorial.webapp.components.attendance-roster :as roster]
   [io.daskul.tutorial.webapp.views.lead-detail.vm :as vm]
   [io.daskul.tutorial.webapp.domain.active-context.subs :as ac.subs]))

(defui trial-roster [{:keys [lead sas tz on-changed]}]
  (let [rows (vm/sa-rows {:lead lead :tz tz} sas)]
    ($ roster/attendance-roster
       {:rows rows
        :on-changed on-changed
        :empty "No trial booked yet for this lead."})))

(defui trial-card
  "Registered for :lead's :main/before slot. ctx is the F-057 slot ctx map;
   only :record and :refetch! are used here."
  [{:keys [record refetch!]}]
  (let [lead record
        lead-id (:id lead)
        tz (urf/use-subscribe [::ac.subs/school-timezone])
        sas (urf/use-subscribe [::agg.subs/all :session-attendee])
        sas-loading? (urf/use-subscribe [::agg.subs/loading? :session-attendee])

        refetch-sas! (uix/use-callback
                      (fn []
                        (when lead-id
                          (rf/dispatch [::agg.events/list :session-attendee {:lead-id lead-id}])))
                      [lead-id])

        ;; After a mark/undo: refetch the SA roster AND the lead (via ctx),
        ;; so the §4.4 cascade advances the page's own record + state display.
        on-changed (uix/use-callback
                    (fn []
                      (refetch-sas!)
                      (refetch!))
                    [refetch-sas! refetch!])]

    ;; Fetch the lead-scoped SA list on mount / lead-id change.
    (uix/use-effect
     (fn []
       (refetch-sas!)
       js/undefined)
     [lead-id refetch-sas!])

    ;; Re-pull the SA roster whenever the lead's lifecycle moves — the lead is
    ;; refetched after any transition (e.g. scheduling a trial, or the §4.4
    ;; cascade), and a state / active-trial change means its SA set changed
    ;; too (mirrors the old hub's use-effect keys).
    (uix/use-effect
     (fn []
       (refetch-sas!)
       js/undefined)
     [(:state lead)
      (get-in lead [:active-trial-session :session-id])
      (get-in lead [:active-trial-session :sa-state])
      refetch-sas!])

    ($ ui/card {:withBorder true :radius "md" :padding "lg" :mb "md"}
       ($ ui/stack {:gap "md"}
          ($ ui/group {:gap "xs" :align "center"}
             ($ icons/icon-calendar {:size 16})
             ($ ui/text {:size "xs" :fw 700 :tt "uppercase" :c "dimmed"
                         :style {:letterSpacing "0.06em"}}
                "Trial Bookings"))
          (if (and sas-loading? (empty? sas))
            ($ ui/centered-loader)
            ($ trial-roster {:lead lead :sas sas :tz tz :on-changed on-changed}))))))

;; ============================================================================
;; Guardian (#767)
;; ============================================================================
;;
;; A Lead's :customer-id is :field/protected and absent from :patch
;; :input-fields, so the generic form neither shows a guardian nor can set one.
;; The aggregate's Guardian display-section names + links an attached one,
;; but nothing on the page could SET one: it could only be set at create, or
;; by the enrollment tx-handler. The in-chat Create Lead slide-over hides the
;; field, so a lead captured from a thread — now most of them — was born
;; guardian-less and stayed that way.
;;
;; Placement (#793): the strip renders ABOVE the form (:main/before, composed
;; with trial-card). It started out below the form's Save row, where it read
;; as part of what Save submits — and the row's "page ends here" signal meant
;; staff never scrolled far enough to see it.

(rf/reg-event-fx ::link-guardian
  (fn [{:keys [db]} [_ lead-id guardian-id]]
    {:db (-> db
             (assoc-in [:leads :linking-guardian?] true)
             (update :leads dissoc :link-guardian-error))
     :http-xhrio (agg.events/build-request
                  {:method :post
                   :path (str "/leads/" lead-id "/link-guardian")
                   :params {:guardian-id guardian-id}
                   :on-success [::link-guardian-success lead-id]
                   :on-failure [::link-guardian-failure]})}))

(rf/reg-event-fx ::link-guardian-success
  (fn [{:keys [db]} [_ lead-id _resp]]
    {:db (-> db
             (assoc-in [:leads :linking-guardian?] false)
             (update :leads dissoc :link-guardian-error))
     :dispatch [::agg.events/get :lead lead-id]}))

(rf/reg-event-db ::link-guardian-failure
  (fn [db [_ error]]
    (-> db
        (assoc-in [:leads :linking-guardian?] false)
        (assoc-in [:leads :link-guardian-error]
                  (or (get-in error [:response :message])
                      "Could not attach that guardian. Please try again.")))))

(rf/reg-sub ::linking-guardian?
  (fn [db _] (get-in db [:leads :linking-guardian?] false)))

(rf/reg-sub ::link-guardian-error
  (fn [db _] (get-in db [:leads :link-guardian-error])))

(defui guardian-card
  "Attach prompt for a guardian-less lead — a slim strip above the form.

   Renders ONLY while the lead has no guardian: once one is attached the
   aggregate's Guardian display-section names and links them, so the strip
   collapses (nil) instead of duplicating it. It exists because attaching
   is the one thing the display-section can't do — and nothing else in the
   product can (see block comment above)."
  [{:keys [record]}]
  (let [lead-id (:id record)
        guardian-id (:customer-id record)
        [picked set-picked!] (uix/use-state nil)
        linking? (urf/use-subscribe [::linking-guardian?])
        error (urf/use-subscribe [::link-guardian-error])]
    (when-not guardian-id
      ($ ui/card {:withBorder true :radius "md" :padding "md" :mb "md"}
         ($ ui/stack {:gap "sm"}
            ($ ui/group {:gap "xs" :align "center"}
               ($ icons/icon-users {:size 16})
               ($ ui/text {:size "xs" :fw 700 :tt "uppercase" :c "dimmed"
                           :style {:letterSpacing "0.06em"}}
                  "Guardian")
               ($ ui/text {:size "sm" :c "dimmed"}
                  "No guardian attached — link one so this lead and the "
                  "family's other records point at the same person."))
            ($ ui/group {:gap "sm" :align "center" :wrap "wrap"}
               ($ ui/box {:style {:flexGrow 1 :minWidth "16rem"}}
                  ($ lookup/lookup-field
                     {:agg-key :lead-link-guardian
                      :field {:name :guardian-id
                              :schema "uuid"
                              ;; :label "" (truthy) blocks lookup's humanized
                              ;; "Guardian Id" fallback yet renders no label,
                              ;; keeping the input the same height as the
                              ;; button beside it — the strip header names
                              ;; the field.
                              :ui {:label ""
                                   :widget :autocomplete
                                   :lookup-url "/guardians/lookup"}}
                      :value picked
                      :on-change set-picked!}))
               ($ ui/button {:disabled (or (nil? picked) linking?)
                             :loading linking?
                             :data-testid "lead-guardian-submit"
                             :onClick #(rf/dispatch [::link-guardian lead-id picked])}
                  "Attach guardian"))
            (when error
              ($ ui/alert {:variant "light" :color "red"
                           :data-testid "lead-guardian-error"}
                 ($ ui/text {:size "sm"} error))))))))

(defui lead-main-before
  "Composed :main/before slot — the page renders one component per band
   (F-057), so the Trial Bookings card and the guardian attach strip
   stack here."
  [ctx]
  ($ :<>
     ($ trial-card ctx)
     ($ guardian-card ctx)))

(detail-slots/register! :lead {:main/before lead-main-before
                               ;; explicit nil so a hot reload drops the
                               ;; pre-#793 below-the-form registration
                               :main/after nil})
